<script setup lang="ts">
import rules from 'loans/services/rules'
import citizenshipOptions from 'loans/types/citizenship.json'

defineProps<{
  content: any
}>()

const values = ref<any>({})
const store = useFormStore()
async function submit(submitValues: any) {
  const [firstName, ...lastNameArray] = submitValues.fullName.trim().split(' ')
  if (lastNameArray.length > 1) submitValues.middleName = lastNameArray.shift()
  submitValues.lastName = lastNameArray.join(' ')
  submitValues.firstName = firstName

  Object.assign(store.values, { ...submitValues })

  await store.updateDemand()
}
</script>

<template>
  <FormStep v-model="values" :submit="submit">
    <template #active>
      <div class="flex flex-col gap-5 p-5 bg-white border-gray-light">
        <h2>{{ content.personalHeader }}</h2>
        <div>{{ content.personalText }}</div>
        <FormFieldText
          name="fullName"
          :rules="rules.fullName"
        />
        <FormFieldAddress group="2" name="placeOfBirth" type="address-city" />
        <FormFieldText
          name="birthNumber"
          :mask="{ mask: '000000/0000' }"
          :rules="rules.birthNumber"
        />
        <FormFieldDropdown
          name="nationality"
          :options="citizenshipOptions"
        />
        <FormFieldDropdown name="maritalStatus" />
        <FormFieldDropdown name="educationLevel" />
        <h4>{{ content.personalContactHeader }}</h4>
        <FormFieldText
          prefix="+420"
          :mask="{ mask: '000 000 000', definitions: { '#': /[+]/ } }"
          name="phoneNumber"
          :rules="rules.phoneNumber"
        />
        <FormFieldText
          suffix="@"
          name="email"
          :rules="rules.emailAddress"
        />
        <FormFieldLoanCheckboxWithError :rules="rules.sanctionTerms" disable-storyblok name="sanctionTerms">
          <StoryblokRichText :document="content.sanctionTerms" />
          <BaseTooltip v-bind="content.sanctionTermsTooltip[0]" />
        </FormFieldLoanCheckboxWithError>
        <div class="flex">
          <FormFieldCheckbox name="marketingTermsConsent" />
          <StoryblokRichText :document="content.marketingTermsAgree" />
        </div>
        <BaseButton :text="content.defaultButtonText" :loading="store.loading" block submit />
        <StoryblokRichText :document="content.gdpr" />
      </div>
    </template>
    <template #incomplete>
      <div class="bg-gray bg-opacity-15 p-5 text-lg text-gray-dark font-bold ">
        {{ content.personalHeader }}
      </div>
    </template>

    <template #complete="{ editStep, mappedValues }">
      <div class="flex flex-col gap-3 bg-gray-300 bg-opacity-20 px-5 py-10">
        <div class="flex justify-between items-center gap-3">
          <span class="text-success">
            <AppIcon
              icon="fal-check-circle"
              size="30"
            />
          </span>
          <div class="font-bold text-lg flex-grow">
            <span>{{ content.personalHeader }}</span>
          </div>
          <div class="flex text-primary items-center cursor-pointer" @click="editStep">
            <AppIcon
              icon="far-pencil"
              size="16"
            />
            <span class="ml-1">Upravit</span>
          </div>
        </div>
        <div class="flex flex-col flex-grow gap-1 text-gray-dark">
          <div class="flex justify-between items-center">
            <span class="hidden md:block">{{ content.personalSummaryFullName }}</span>
            <span class="font-medium">{{ mappedValues.fullName }}</span>
          </div>
          <div class="flex justify-between items-center">
            <span class="hidden md:block">{{ content.personalSummaryPlaceOfBirth }}</span>
            <span class="font-medium">{{ mappedValues.placeOfBirth }}</span>
          </div>
          <div class="flex justify-between items-center">
            <span class="hidden md:block">{{ content.personalSummaryBirthNumber }}</span>
            <span class="font-medium"><span class="font-medium md:hidden">RČ:</span> {{ mappedValues.birthNumber.substr(0, 6) }}/{{ mappedValues.birthNumber.substr(6) }}</span>
          </div>
          <div class="flex justify-between items-center">
            <span class="hidden md:block">{{ content.personalSummaryNationality }}</span>
            <span class="font-medium">{{ mappedValues.nationality }}</span>
          </div>
          <div class="flex justify-between items-center">
            <span class="hidden md:block">{{ content.personalSummaryMaritalStatus }}</span>
            <span class="font-medium">{{ mappedValues.maritalStatus }}</span>
          </div>
          <div class="flex justify-between items-center">
            <span class="hidden md:block">{{ content.personalSummaryEducationLevel }}</span>
            <span class="font-medium">{{ mappedValues.educationLevel }}</span>
          </div>
          <div class="flex flex-col mt-2 md:flex-row md:justify-between md:items-center md:mt-0">
            <span class="block md:hidden">{{ content.personalContactHeader }}</span>
            <span class="hidden md:block">Telefon</span>
            <span class="font-medium">+420 {{ Number(mappedValues.phoneNumber).toLocaleString('cs') }}</span>
          </div>
          <div class="flex justify-between items-center">
            <span class="hidden md:block">E-mail</span>
            <span class="font-medium">{{ mappedValues.email }}</span>
          </div>
        </div>
      </div>
    </template>
  </FormStep>
</template>
